import React, { useState, useEffect, useContext } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import { is_empty } from "utils/validations";

import { WebpageCreationContext } from "../../../..";
import {
  MODAL_TYPES,
  GALLERY_TYPES,
  SIDEBAR_SECTIONS,
  SIDEBAR_KEYS,
  GalleryConfig,
  SECTIONKEY_TO_MISC_KEY,
} from "../../../../constants";
import api from "../../../../../../../data/APIs";

import style from "./Gallery.module.css";
import baseStyle from "../../style.module.css";
import { GalleryItemDraggable } from "./GalleryItem/GalleryItem";

import { creator_miscellaneous_data_types } from "features/Common/modules/CreatorData/CreatorData.constants";
import SectionVisibilityToggle from "../hideShow";
import { handleVisibilityToggle } from "../../utils";
import useVisibilityMutations from "../../useVisibilityMutations.hook";

const GalleryList = ({
  stateProp = [],
  handleEdit,
  handleDelete,
  someItemBeingDragged,
  disableAllEdits,
}) =>
  (stateProp || []).map((item, index) => (
    <GalleryItemDraggable
      key={item.uuid}
      item={item}
      index={index}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      someItemBeingDragged={someItemBeingDragged}
      disableAllEdits={disableAllEdits}
    />
  ));

export const Gallery = (props) => {
  const {
    fetchCall,
    pushNotif,
    templateData,
    gallery: { galleryData, setGalleryData },

    modal: { openModal, closeModal },

    miscellaneous_data,
    fetchMiscellaneousData,
    set_miscellaneous_data,
  } = useContext(WebpageCreationContext);

  const galleryTitle = SIDEBAR_SECTIONS[SIDEBAR_KEYS.GALLERY].title({
    templateName: templateData.slug,
  });

  const { setField, disableAllEdits } = props;

  const [state, setState] = useState(galleryData || []);
  const [dragElement, setDragElement] = useState(null);
  const [isSaving, setSaving] = useState(null);
  const [errorObject, setErrorObject] = React.useState({});
  const [visible, set_visible] = React.useState(true);

  // Mutations for visibility toggle
  const { createMutation, updateMutation } = useVisibilityMutations();

  const reorder = (list, { from: startIndex, to: endIndex }) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result.map((item, index) => ({
      ...item,
      priority: result.length - index - 1,
    }));
  };

  const handleDragStart = (result) => {
    setDragElement(result.draggableId);
    if (window.navigator.vibrate) {
      window.navigator.vibrate(100);
    }
  };

  const handleDragEnd = (result) => {
    setDragElement(null);
    if (
      !result?.destination ||
      result?.destination.index === result?.source.index
    ) {
      return;
    }

    const newState = reorder(state, {
      from: result.source.index,
      to: result.destination.index,
    });

    setState(newState);
    setGalleryData(newState);
    handleReorder(newState);
  };

  const handleReorder = (stateProp) => {
    fetchCall({
      url: api.reorder_gallery,
      method: "POST",
      payload: {
        gallery_items: stateProp.map((item, index) => ({
          priority: stateProp.length - index - 1,
          uuid: item.uuid,
        })),
      },
      errorCallback: () => {
        pushNotif(`Error while updating ${galleryTitle.toLowerCase()} order`);
      },
    });
  };

  const handleCreate = () => {
    openModal(MODAL_TYPES.GALLERY, {
      modalLabels: {
        modalTitle: "Add new media",
        showDividers: true,
      },
      data: {
        type: GALLERY_TYPES.IMAGE,
        media: null, // Image/Video URL
      },
      template: templateData.slug,
      primaryAction: ({ data: createData }) => {
        setSaving("Creating");
        closeModal();
        fetchCall({
          url: api.create_gallery,
          method: "POST",
          payload: {
            gallery_item: {
              ...createData,
            },
          },
          successCallback: ({ uuid }) => {
            pushNotif(`${galleryTitle} created`);
            setGalleryData([...galleryData, { ...createData, uuid }]);
            setSaving(null);
          },
          errorCallback: (err) => {
            pushNotif(
              err?.body?.message ||
                `Error while updating ${galleryTitle.toLowerCase()}`
            );
            setSaving(null);
          },
        });
      },
      secondaryAction: closeModal,
    });
  };

  const handleEdit =
    (item) =>
    (loadWithPreview = false) => {
      openModal(MODAL_TYPES.GALLERY, {
        modalLabels: {
          modalTitle: "Edit media",
          showDividers: true,
        },
        loadWithPreview,
        template: templateData.slug,
        data: item,
        primaryAction: ({ data: updatedData }) => {
          setSaving("Updating");
          closeModal();
          fetchCall({
            url: api.update_gallery,
            method: "POST",
            payload: {
              uuid: item.uuid,
              gallery_item: {
                ...updatedData,
              },
            },
            successCallback: () => {
              pushNotif(`${galleryTitle} Item updated`);
              setGalleryData([
                ...galleryData.map((t) =>
                  t.uuid === item.uuid ? updatedData : t
                ),
              ]);
              setSaving(null);
            },
            errorCallback: (err) => {
              pushNotif(
                err?.body?.message ||
                  `"Error while updating ${galleryTitle.toLowerCase()} item"`
              );
              setSaving(null);
            },
          });
        },
        secondaryAction: closeModal,
      });
    };

  const handleDelete = (item) => () => {
    openModal(MODAL_TYPES.CONFIRM, {
      modalLabels: {
        modalTitle: "Confirm Action",
        primaryText: "Cancel Delete",
        secondaryText: "Delete",
        showDividers: false,
      },
      body: `Are you sure you want to delete this media item?`,
      hideCtaIcons: true,
      primaryAction: closeModal,
      secondaryAction: () => {
        setSaving("Deleting");
        closeModal();
        fetchCall({
          url: api.delete_gallery,
          method: "POST",
          payload: {
            uuid: item.uuid,
          },
          successCallback: () => {
            pushNotif(`${galleryTitle} item deleted successfully!`);
            setState((stateProp) =>
              stateProp.filter((t) => t.uuid !== item.uuid)
            );
            setGalleryData(galleryData.filter((i) => i.uuid !== item.uuid));
            setSaving(null);
          },
          errorCallback: (error) => {
            pushNotif(
              error?.body?.message ||
                `Error while deleting ${galleryTitle.toLowerCase()} item`
            );
            setSaving(null);
          },
        });
      },
    });
  };

  useEffect(() => {
    try {
      if (is_empty(miscellaneous_data?.gallery)) {
        // SAFETY NET → default visible = true
        set_visible(true);
        return;
      }

      const temp = miscellaneous_data.gallery[0];

      set_visible(
        typeof temp?.data?.visible === "boolean" ? temp.data.visible : true
      );
    } catch {
      set_visible(true);
    }
  }, [miscellaneous_data]);

  useEffect(() => {
    setState(galleryData);
  }, [galleryData]);

  useEffect(() => {
    if (!is_empty(errorObject)) setErrorObject({});
  }, [isSaving]);

  if (!galleryData) {
    return <div>Loading...</div>;
  }

  const onToggleVisibility = () =>
    handleVisibilityToggle({
      currentVisible: visible,
      setVisible: set_visible,
      miscellaneousData: miscellaneous_data,
      sectionKey: SECTIONKEY_TO_MISC_KEY[SIDEBAR_KEYS.GALLERY],
      sectionType: creator_miscellaneous_data_types.PORTFOLIO,
      defaultSectionName: SIDEBAR_SECTIONS[SIDEBAR_KEYS.GALLERY].title,
      fetchMiscellaneousData,
      set_miscellaneous_data,
      createMutation: createMutation,
      updateMutation: updateMutation,
    });

  const showHeading = GalleryConfig.showHeading.includes(templateData.slug);

  return (
    <>
      <SectionVisibilityToggle
        visible={visible}
        disabled={disableAllEdits}
        onToggle={onToggleVisibility}
      />

      <div className={baseStyle.fieldContainer}>
        <div className={baseStyle.label}>
          What would you like to call this section?
        </div>
        <div className={baseStyle.field}>
          <OutlinedInput
            placeholder="Enter the section heading here"
            className={baseStyle.input}
            disabled={disableAllEdits}
            error={errorObject?.sectionNameLength}
            value={props?.state?.custom_headings?.gallery_title || ""}
            inputProps={{
              maxLength: GalleryConfig.maxTitleLength[templateData.slug],
            }}
            onChange={(e) => {
              const value = e?.target?.value;
              if (
                value?.length >= GalleryConfig.maxTitleLength[templateData.slug]
              ) {
                setErrorObject((prev) => ({
                  ...prev,
                  sectionNameLength: `Maximum field length of ${
                    GalleryConfig.maxTitleLength[templateData.slug]
                  } reached.`,
                }));
              } else {
                setErrorObject((prev) => ({
                  ...prev,
                  sectionNameLength: false,
                }));
              }
              setField("custom_headings", {
                ...props?.state?.custom_headings,
                gallery_title: e?.target?.value || "",
              });
            }}
          />
          {errorObject?.sectionNameLength && (
            <div className={baseStyle.error}>
              {errorObject?.sectionNameLength}
            </div>
          )}
        </div>
      </div>

      {showHeading ? (
        <div>
          <div className={baseStyle.label}>Heading</div>
          <div className={baseStyle.field}>
            <OutlinedInput
              placeholder="Eg: Memories worth sharing"
              className={baseStyle.input}
              disabled={disableAllEdits}
              error={errorObject?.sectionHeadingLength}
              value={props?.state?.custom_headings?.gallery_description || ""}
              inputProps={{
                maxLength: GalleryConfig.maxHeadingLength[templateData.slug],
              }}
              onChange={(e) => {
                const value = e?.target?.value;
                if (
                  value?.length >=
                  GalleryConfig.maxHeadingLength[templateData.slug]
                ) {
                  setErrorObject((prev) => ({
                    ...prev,
                    sectionHeadingLength: `Maximum field length of ${
                      GalleryConfig.maxHeadingLength[templateData.slug]
                    } reached.`,
                  }));
                } else {
                  setErrorObject((prev) => ({
                    ...prev,
                    sectionHeadingLength: false,
                  }));
                }
                setField("custom_headings", {
                  ...props?.state?.custom_headings,
                  gallery_description: e?.target?.value || "",
                });
              }}
            />
            <div className={baseStyle.error}>
              {errorObject?.sectionHeadingLength}
              <br />
              <br />
            </div>
          </div>
        </div>
      ) : null}
      <div className={`${baseStyle.info} tw-my-[8px]`}>
        <InfoOutlined className={baseStyle.infoIcon} />
        The {galleryTitle.toLowerCase()} items can be dragged up and down using
        the
        <DragIndicatorIcon className={baseStyle.dragIcon} /> icon to change the
        order in which they appear.
      </div>
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Droppable droppableId="galleryList">
          {(droppableProvided) => (
            <div
              className={`${style.galleryContainer} ${
                dragElement ? style.dragHappening : ""
              }`}
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              <GalleryList
                stateProp={state}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                someItemBeingDragged={dragElement}
                disableAllEdits={disableAllEdits}
              />
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
        {isSaving ? (
          <div className={style.savingStatus}>
            {isSaving}&nbsp;
            <CircularProgress color="secondary" size={24} thickness={2} />
          </div>
        ) : null}
        <Button
          className={style.addButton}
          color="primary"
          disabled={isSaving || disableAllEdits}
          onClick={handleCreate}
        >
          <AddCircleIcon className={style.addIcon} /> Add new item
        </Button>
      </DragDropContext>
    </>
  );
};
